import { useState } from 'react';
import {
    getLatestPricePerformancePoint,
    type PricePerformancePoint,
    type RelativePerformanceResponse,
} from '../api/chartDataTypes';
import { formatReportDate, reportTypeLabel } from '../utils/perf10kFormat';
import { buildPricePerformanceSummary } from '../utils/pricePerformanceFormat';
import { ChartDataSectionShell } from './ChartDataSectionShell';
import { DataPointSelector } from './DataPointSelector';

type RelativePerformanceChartPanelProps = {
    response: RelativePerformanceResponse;
};

/**
 * Relative-Performance orchestrator for STARS_ENRN securities. The payload and
 * layout are identical to Price Performance (STARS_ENRT) — only `chart_type`
 * and the backend title differ — so this reuses the same summary builder and
 * data-point selector. STARS rank, target price, and index pct_change snap to
 * the nearest entry at-or-before the chosen date (see
 * `buildPricePerformanceSummary`).
 */
export function RelativePerformanceChartPanel({ response }: RelativePerformanceChartPanelProps) {
    const { security, title, report_date, data } = response;
    const latestPoint = getLatestPricePerformancePoint(response);

    const [selectedPoint, setSelectedPoint] = useState<PricePerformancePoint | null>(null);

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;

    const pointForSummary = selectedPoint ?? latestPoint;
    const summaryParts = pointForSummary ? buildPricePerformanceSummary(response, pointForSummary) : null;
    const summary = summaryParts?.text ?? 'No data available.';
    const summaryHTML = summaryParts?.html;

    return (
        <ChartDataSectionShell
            title={headerTitle}
            subtitle={headerSubtitle}
            summary={summary}
            summaryHTML={summaryHTML}>
            {data.company_price.length > 0 ? (
                <DataPointSelector<PricePerformancePoint>
                    points={data.company_price}
                    idPrefix='relative_performance'
                    onSelectedPointChange={setSelectedPoint}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
